import { BADGE_TYPE, TROPHY_TYPE } from "@repo/shared";
import { cva } from "class-variance-authority";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import { camelCase } from "lodash-es";
import { useTranslation } from "react-i18next";

import { getBadgeImagePath } from "~/assets/badges";
import { Icon } from "~/components/Icon";
import { useMediaQuery } from "~/hooks/useMediaQuery";
import { SearchBadge } from "~/modules/Client/Courses/SearchLesson/SearchBadge";
import { getBadgeVariant } from "~/utils/getBadgeVariant";

import type { BadgeType, TrophyType } from "@repo/shared";

export type BadgeGraphicBadge = {
  id: string;
  name: string;
  badgeType: BadgeType;
  trophyType: TrophyType;
  tier: number;
  completedTierName: string;
  badgeAvatarId: string | null;
  description: string | null;
  descriptionAfterShare: string | null;
  subjectIds: string[] | null;
  subjectCodes: string[] | null;
  schoolClassName: string | null;
};

const popoverBadgeVariants = cva(
  "details flex w-max gap-x-1 items-center justify-between px-2 py-[1.5px] border rounded-lg mx-auto",
  {
    variants: {
      variant: {
        1: "gamification__badge-1",
        2: "gamification__badge-2",
        3: "gamification__badge-3",
        4: "gamification__badge-4",
        [TROPHY_TYPE.STARTER]: "gamification__badge-starter",
      },
    },
    defaultVariants: {
      variant: 1,
    },
  },
);

function formatDatePolish(date: Date): string {
  return format(date, "d MMMM yyyy 'r.'", { locale: pl });
}

interface BadgeGraphicProps {
  badge: BadgeGraphicBadge;
  date?: Date;
  isShared?: boolean;
}

export const BadgeGraphic = ({ badge, date, isShared = false }: BadgeGraphicProps) => {
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const { t } = useTranslation();

  const variant = badge && 1 <= badge.tier && badge.tier <= 4 ? (badge.tier as 1 | 2 | 3 | 4) : 1;
  const badgeImage = badge ? getBadgeImagePath(badge.badgeAvatarId) : undefined;

  return (
    <div className="rounded-3xl bg-white px-3 py-6 2xl:p-[52px]">
      <div
        className={popoverBadgeVariants({
          variant: badge.trophyType === TROPHY_TYPE.STARTER ? TROPHY_TYPE.STARTER : variant,
        })}
      >
        {badge.trophyType === TROPHY_TYPE.STARTER ? (
          <p>Startowicz</p>
        ) : (
          <p>
            <span className="font-bold">
              {t(`userProgressPage.gamificationAchievements.${camelCase(badge.trophyType)}.name`)}:
            </span>{" "}
            {t("gamification.level", { level: variant })}
          </p>
        )}
      </div>
      <img
        src={badgeImage}
        alt={badge?.name ?? "badge"}
        className="mx-auto mt-3 h-auto object-contain lg:mt-6 2xl:mt-6"
        style={{
          width: isDesktop ? "335px" : "286px",
        }}
      />
      {badge.schoolClassName && badge?.badgeType === BADGE_TYPE.LEARNING_TROPHY && (
        <SearchBadge
          variant={getBadgeVariant(badge.schoolClassName)}
          className="mx-auto mt-3 w-max 2xl:mt-4"
        />
      )}
      <div className="mx-auto w-full max-w-[334px] text-center 2xl:max-w-[432px]">
        <p className="mt-5 font-madimi text-sm leading-[120%] text-[#2F076E] 2xl:mt-10">
          {formatDatePolish(date ?? new Date())}
        </p>
        <h1 className="h5 2xl:h4 mt-3 text-[#5E12C5] 2xl:mt-4">
          {t("gamification.congratulationsTitle")}
        </h1>
        <p className="body-sm 2xl:body-base mt-1 text-[#5E12C5] 2xl:mt-3">
          {isShared ? badge.descriptionAfterShare : badge.description}
        </p>
        <a href="https://skumani.pl/" target="_blank" rel="noopener noreferrer">
          <Icon name="LOGO" className="mx-auto mt-2 h-6 w-auto text-[#5E12C5] 2xl:mt-8" />
          <span className="sr-only">Skumani.pl {t("common.other.opensInNewTab")}</span>
        </a>
      </div>
    </div>
  );
};
